import React from "react";
import { FatButton, LooseCheckboxInput, PlainInputWrapper } from "../__Internal__";
import { useHTMLInputElementValidationProjection } from "../hooks/useHTMLInputElementValidationProjection";
import { PropError } from "../PropX";
import { useCheckboxGroupState } from "./CheckboxStateContext";

type Props = {
  /**
   * The name of the checkbox field.
   */
  name: string;
  /**
   * The value of the checkbox field. Necessary, but kind of useless for controlled form inputs.
   */
  value: string;

  /**
   * The title of the checkbox.
   */
  title: React.ReactNode;

  /**
   * The subtitle of the checkbox.
   */
  subtitle?: React.ReactNode;

  /**
   * Whether the checkbox is checked.
   */
  checked: boolean;

  /**
   * Whether the checkbox is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * The icon to render on the left side of the radio button.
   * @default null
   */
  icon?: string;

  /**
   * Whether the checkbox is required.
   * @default false
   */
  required?: boolean;
  /**
   * Whether the checkbox is indeterminate.
   * @default false
   */
  indeterminate?: boolean;

  /**
   * The event for which to validate the input.
   * @default "manual"
   */
  validateOn?: "change" | "blur" | "manual";

  /**
   * When the checkbox changes from checked to unchecked or vice versa.
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;

  /**
   * Testing utility tag.
   * @default "checkbox-input"
   */
  "data-testid"?: string;
};

export const CheckboxInput: React.FC<Props> = (props) => {
  const {
    name,
    value,
    title,
    subtitle,
    checked,
    validateOn = "manual",
    icon = null,
    disabled = false,
    required = false,
    indeterminate = false,
  } = props;
  const testId = props["data-testid"] ?? "checkbox-input";

  const { horizontal } = useCheckboxGroupState();
  const ref = React.useRef<HTMLInputElement>(null);
  const [errors, propsForInput] = useHTMLInputElementValidationProjection(props, { ref, validateOn });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { invalid, ...restPropsForInput } = propsForInput;

  const id = `${name}-${value}`;

  /**
   * React doesn't support "indeterminate" state for checkboxes, so we have to do it manually.
   */
  React.useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [ref.current, indeterminate, checked]);

  return (
    <FatButton.BaseContainer $horizontal={horizontal}>
      <PlainInputWrapper
        ref={ref}
        id={id}
        value={value}
        title={title}
        subtitle={subtitle}
        icon={icon}
        checked={checked}
        disabled={disabled}
      >
        {(childProps) => {
          return (
            <LooseCheckboxInput
              {...childProps}
              type="checkbox"
              name={name}
              required={required}
              data-testid={testId}
              {...restPropsForInput}
            />
          );
        }}
      </PlainInputWrapper>
      {PropError.renderGroupAfterInput(errors)}
    </FatButton.BaseContainer>
  );
};

CheckboxInput.displayName = "CheckboxInput";
